import React from 'react';
import BaseModal from 'molecules/BaseModal/BaseModal';
import Stack from 'atoms/Layout/Stack/Stack';
import Typography from 'atoms/Typography/Typography';
import Button from 'atoms/Button/Button';
import { Language, useI18nContext } from 'src/hooks/useI18nContext';

interface LanguageSwitcherPopupProps {
  visible: boolean;
  detectedLanguage: Language;
  onAccept: () => void;
  onDismiss: () => void;
}

const languageNames: Record<Language, string> = {
  en: 'English',
  es: 'Español',
  de: 'Deutsch',
  fr: 'Français',
  it: 'Italiano',
  nl: 'Nederlands',
  pt: 'Português',
};

// Translations for the language switcher in each supported language
// This allows us to show the popup in the detected language, not the URL language
const languageSwitcherTranslations: Record<Language, {
  title: string;
  descriptionStart: string;
  descriptionEnd: string;
  stayButton: (currentLang: string) => string;
  switchButton: (targetLang: string) => string;
}> = {
  en: {
    title: 'Switch Language?',
    descriptionStart: 'We detected your browser language is',
    descriptionEnd: 'Would you like to view this site in',
    stayButton: (currentLang) => `Stay in ${currentLang}`,
    switchButton: (targetLang) => `Switch to ${targetLang}`,
  },
  es: {
    title: '¿Cambiar idioma?',
    descriptionStart: 'Detectamos que el idioma de tu navegador es',
    descriptionEnd: '¿Te gustaría ver este sitio en',
    stayButton: (currentLang) => `Mantener en ${currentLang}`,
    switchButton: (targetLang) => `Cambiar a ${targetLang}`,
  },
  de: {
    title: 'Sprache wechseln?',
    descriptionStart: 'Wir haben festgestellt, dass Ihre Browsersprache',
    descriptionEnd: 'ist. Möchten Sie diese Website auf',
    stayButton: (currentLang) => `Auf ${currentLang} bleiben`,
    switchButton: (targetLang) => `Zu ${targetLang} wechseln`,
  },
  fr: {
    title: 'Changer de langue ?',
    descriptionStart: 'Nous avons détecté que la langue de votre navigateur est',
    descriptionEnd: 'Souhaitez-vous consulter ce site en',
    stayButton: (currentLang) => `Rester en ${currentLang}`,
    switchButton: (targetLang) => `Passer à ${targetLang}`,
  },
  it: {
    title: 'Cambiare lingua?',
    descriptionStart: 'Abbiamo rilevato che la lingua del tuo browser è',
    descriptionEnd: 'Vuoi visualizzare questo sito in',
    stayButton: (currentLang) => `Resta in ${currentLang}`,
    switchButton: (targetLang) => `Passa a ${targetLang}`,
  },
  nl: {
    title: 'Taal wisselen?',
    descriptionStart: 'We hebben gedetecteerd dat uw browsertaal',
    descriptionEnd: 'is. Wilt u deze site in het',
    stayButton: (currentLang) => `Blijf in het ${currentLang}`,
    switchButton: (targetLang) => `Wissel naar ${targetLang}`,
  },
  pt: {
    title: 'Trocar idioma?',
    descriptionStart: 'Detectamos que o idioma do seu navegador é',
    descriptionEnd: 'Gostaria de ver este site em',
    stayButton: (currentLang) => `Ficar em ${currentLang}`,
    switchButton: (targetLang) => `Trocar para ${targetLang}`,
  },
};

const LanguageSwitcherPopup = ({
  visible,
  detectedLanguage,
  onAccept,
  onDismiss,
}: LanguageSwitcherPopupProps): JSX.Element => {
  const { language: currentLanguage } = useI18nContext();

  // Use translations for the detected language (not the current URL language)
  const translations = languageSwitcherTranslations[detectedLanguage];
  const detectedLanguageName = languageNames[detectedLanguage];
  const currentLanguageName = languageNames[currentLanguage];

  return (
    <BaseModal
      visible={visible}
      onClose={onDismiss}
      baseWidth={500}
    >
      <Stack
        direction='column'
        padding='spacing-7xl'
        paddingMd='spacing-6xl'
        gap='spacing-4xl'
        background='bg-primary'
        borderRadius='radius-2xl'
      >
        <Stack
          direction='column'
          gap='spacing-lg'
        >
          <Typography
            type='heading'
            size='sm'
            textAlign='center'
            skipTranslation
          >
            {translations.title}
          </Typography>
          <Typography
            type='paragraph'
            size='md'
            textAlign='center'
            color='text-secondary'
            skipTranslation
          >
            {translations.descriptionStart}{' '}
            <strong>{detectedLanguageName}</strong>. {translations.descriptionEnd}{' '}
            {detectedLanguageName}?
          </Typography>
        </Stack>

        <Stack
          direction='row'
          gap='spacing-lg'
          justify='center'
          directionSm='column'
        >
          <Button
            variant='secondary'
            size='md'
            onClick={onDismiss}
            skipTranslation
          >
            {translations.stayButton(currentLanguageName)}
          </Button>
          <Button
            variant='primary'
            size='md'
            onClick={onAccept}
            skipTranslation
          >
            {translations.switchButton(detectedLanguageName)}
          </Button>
        </Stack>
      </Stack>
    </BaseModal>
  );
};

export default LanguageSwitcherPopup;
